/**
 * Zona de arrastrar-y-soltar (o click) para cargar un XML o PDF de factura y
 * usarlo como ayuda para prellenar una identidad fiscal nueva.
 *
 * Nunca guarda nada por sí sola: solo llama a `onParsed` con lo que pudo
 * leer, y quien la usa abre el formulario existente para que la persona
 * revise y confirme.
 */

import { useRef, useState } from 'react';
import { Button } from '../ui';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  extractPdfText, parseFiscalPdfText, parseFiscalXml, prefillFieldCount, type BillingPrefill,
} from '@/lib/fiscalImport';

type Status =
  | { kind: 'idle' }
  | { kind: 'reading' }
  | { kind: 'found' }
  | { kind: 'partial' }
  | { kind: 'unreadable' }
  | { kind: 'unsupported' };

export function FiscalImportDropzone({ onParsed, onManual }: {
  onParsed: (fields: BillingPrefill) => void;
  onManual: () => void;
}) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setStatus({ kind: 'reading' });

    const isXml = file.type.includes('xml') || file.name.toLowerCase().endsWith('.xml');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isXml && !isPdf) {
      setStatus({ kind: 'unsupported' });
      return;
    }

    try {
      let fields: BillingPrefill | null = null;
      if (isXml) {
        fields = parseFiscalXml(await file.text());
      } else {
        fields = parseFiscalPdfText(await extractPdfText(file));
      }

      if (!fields) {
        setStatus({ kind: 'unreadable' });
        return;
      }

      const complete = prefillFieldCount(fields) >= 4;
      setStatus({ kind: complete ? 'found' : 'partial' });
      onParsed(fields);
    } catch {
      setStatus({ kind: 'unreadable' });
    }
  };

  const message: Record<Status['kind'], string | null> = {
    idle: null,
    reading: 'Leyendo archivo…',
    found: 'Datos encontrados. Revisá el formulario antes de guardar.',
    partial: 'Se encontraron algunos datos. Faltan campos por revisar en el formulario.',
    unreadable: 'No se pudo leer el archivo. Podés cargarlo de nuevo o completar el formulario a mano.',
    unsupported: 'Archivo no compatible. Solo se aceptan XML o PDF de factura.',
  };

  return (
    <div>
      <div
        className="fiscal-dropzone"
        data-drag-over={dragOver || undefined}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) { haptic.light(); void handleFile(file); } // HAPTIC_FEEDBACK: drop válido, no el resultado del parseo
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xml,.pdf,application/xml,text/xml,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
        <span className="fiscal-dropzone__icon">⇪</span>
        <strong>Arrastrar XML o PDF aquí</strong>
        <span className="small muted">o hacé click para elegir un archivo</span>
      </div>

      {message[status.kind] && (
        <p
          className="small mt-1"
          style={{ color: status.kind === 'unreadable' || status.kind === 'unsupported'
            ? 'var(--danger)' : status.kind === 'partial' ? 'var(--warn)' : undefined }}
        >
          {message[status.kind]}
        </p>
      )}

      <div className="mt-1">
        <Button onClick={onManual}>Añadir identidad fiscal manualmente</Button>
      </div>
    </div>
  );
}
